import * as yup from "yup";
import { create } from "zustand";

// Define the store state type
type AllocationDndStore = {
  items: { main: string[] };
  itemsOriginal: Record<string, string[]>;
  unallocatedCostsOriginal: string;
  hasUnallocated: boolean;
  unallocatedCosts: string;
  isGroup: boolean;
  isAllocationEditor: boolean;
  setIsAllocationEditor: (value: boolean) => void;
  setIsGroup: (value: boolean) => void;
  setHasUnallocated: (value: boolean) => void;
  setUnallocated: (value: string) => void;
  addItem: (item: string) => void;
  insertItem: (item: string, index: number) => void;
  removeItem: (item: string) => void;
  setItems: (items: Record<string, string[]>) => void;
  setAllocationName: (value: string) => void;
  clear: () => void;
  resetItemsOriginal: () => void;
  setUnallocatedOriginal: (value: string) => void;
  setAllocationDescription: (value?: string) => void;
  allocationName: string;
  allocationDescription: string;
  fieldErrors: Record<string, string>;
  hasUnallocatedOriginal: boolean;
};

const formSchema = yup.object({
  unallocatedCosts: yup.string().trim().min(1).required("Unallocated name is required"),
});

// Create the store
const useAllocationDndStore = create<AllocationDndStore>((set, get) => ({
  // Initial state
  items: { main: [] },
  itemsOriginal: { main: [] },
  unallocatedCostsOriginal: "Unallocated",
  hasUnallocatedOriginal: false,
  hasUnallocated: false,
  unallocatedCosts: "Unallocated",
  fieldErrors: {},
  allocationName: "",
  allocationDescription: "",
  isGroup: false,
  isAllocationEditor: true,

  setIsAllocationEditor: (value) => {
    set(() => ({ isAllocationEditor: value }));
  },

  resetItemsOriginal: () => {
    set((state) => ({ itemsOriginal: { main: state.items.main } }));
  },

  setIsGroup: (value) => {
    set(() => ({ isGroup: value }));
  },

  setHasUnallocated: (value) => {
    set(() => ({ hasUnallocated: value }));
  },

  setAllocationName: (value) => {
    set(() => ({ allocationName: value }));
  },

  setAllocationDescription: (value) => {
    set(() => ({ allocationDescription: value }));
  },

  setUnallocated: async (value) => {
    set(() => ({ unallocatedCosts: value }));
    try {
      const { unallocatedCosts } = get();
      await formSchema.validate({ unallocatedCosts }, { abortEarly: false });
      set({ fieldErrors: {} });
      // If validation passes, update external save permission store
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const errors = error.inner.reduce(
          (acc, err) => ({
            ...acc,
            ...(err.path ? { [err.path]: err.message } : {}),
          }),
          {}
        );
        set({ fieldErrors: errors });
      }
    }
  },

  setUnallocatedOriginal: (value) => {
    set(() => ({ unallocatedCostsOriginal: value, hasUnallocatedOriginal: !!value }));
  },

  addItem: (item) => {
    set((state) => ({
      items: { main: [...state.items.main, item] },
    }));
  },

  insertItem: (item, index) => {
    set((state) => {
      const newItems = [...state.items.main];
      newItems.splice(index, 0, item);
      return { items: { main: newItems } };
    });
  },

  removeItem: (item) => {
    set((state) => ({ items: { main: state.items.main.filter((id) => id !== item) } }));
  },

  setItems: (items) => {
    set({ items: { main: items.main } });
  },
  clear: () => {
    set({
      allocationName: "",
      allocationDescription: "",
      unallocatedCosts: "Unallocated",
      hasUnallocated: false,
      isGroup: false,
      isAllocationEditor: true,
      items: { main: [] },
      itemsOriginal: { main: [] },
      unallocatedCostsOriginal: "Unallocated",
      hasUnallocatedOriginal: false,
      fieldErrors: {},
    });
  },
}));

export default useAllocationDndStore;
